// PR-22: client-side error capture. Hooks window.onerror /
// window.onunhandledrejection and POSTs the payload to
// /api/errors/report (public endpoint; cookie auth is best-effort
// server-side). Throttled at the source so a runaway render loop
// can't bury the table — the server's per-IP limiter is the second
// line of defence.
//
// PR-31: also exposes `reportClientError(err, context)` for CAUGHT
// errors — try/catch blocks that surface a toast and would otherwise
// be invisible to the central monitor (since they never fire
// `unhandledrejection`). Use it at every call site that catches and
// renders an error to the user. Auto-includes the recent request_id
// ring from `observability/request_ids.ts` so each error row can be
// correlated to the matching backend log span.

import { recentRequestIds } from './observability/request_ids'
import { recentCrumbs } from './observability/breadcrumbs'

interface ErrorReportPayload {
  message: string
  stack_trace: string | null
  location: string
  user_agent: string
  context: Record<string, unknown>
}

/**
 * Inspect an unknown thrown value and extract every diagnostic the
 * server might need to identify the root cause. Returns a serializable
 * payload suitable for /api/errors/report's `context` field.
 *
 * The point is to handle the "non-Error throw" case explicitly:
 * browser extensions, third-party hooks, and some Capacitor plugins
 * sometimes throw plain objects like `{message: "network error"}`.
 * Those slip past `e instanceof Error` checks and produce phantom UI
 * states that the central monitor can't see.
 */
function describeThrown(err: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = {
    is_error_instance: err instanceof Error,
    is_type_error: err instanceof TypeError,
    typeof: typeof err,
  }
  if (err === null || err === undefined) {
    out.value = String(err)
    return out
  }
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>
    out.ctor = (o.constructor as { name?: string } | undefined)?.name ?? null
    // Prototype chain depth helps spot proxies / cross-realm objects
    // that confuse `instanceof`.
    let proto: object | null = Object.getPrototypeOf(o) as object | null
    const chain: string[] = []
    while (proto && chain.length < 5) {
      chain.push(proto.constructor?.name ?? 'unknown')
      proto = Object.getPrototypeOf(proto) as object | null
    }
    out.proto_chain = chain
    // Own-property dump (capped) — surfaces non-standard fields like
    // `code`, `errno`, `cause`, `request_id`.
    const own: Record<string, unknown> = {}
    for (const key of Object.getOwnPropertyNames(o).slice(0, 20)) {
      const v = o[key]
      if (v === null || ['string', 'number', 'boolean'].includes(typeof v)) {
        own[key] = v
      } else {
        own[key] = `[${typeof v}]`
      }
    }
    out.own_props = own
  } else {
    out.value = String(err)
  }
  return out
}

/**
 * Report a CAUGHT error to the central monitor. Use at every try/catch
 * site that surfaces a toast — those are the errors that don't fire
 * `unhandledrejection` and would otherwise be invisible.
 *
 * `context.action` is the human-readable action that triggered it
 * (e.g. "mobile.capture.send"). `context.request_id` (if known) lets
 * the server correlate to its own structured logs.
 */
export function reportClientError(
  err: unknown,
  context: Record<string, unknown> = {},
): void {
  const message = err instanceof Error ? err.message : String((err as { message?: unknown })?.message ?? err ?? '')
  const stack = err instanceof Error ? (err.stack ?? null) : null
  // PR-31: auto-attach the recent request_id ring so the row can be
  // joined back to the backend's structured logs (request_id.rs spans).
  // We don't try to pick "the" right id from the ring — give the
  // triager the last 10 with their paths and let them eyeball.
  const recent_request_ids = recentRequestIds()
  // PR-31: breadcrumbs — last ~30 user/network events. Same idea as
  // request_ids but covers user actions (clicks) too. The combination
  // tells you "user clicked Send → POST /messages started → 0ms
  // later threw" — the chain you need to spot a fetch interceptor.
  const recent_crumbs = recentCrumbs()
  // Echo to console so a developer in DevTools sees it immediately.
  // Keep the prefix so it's grep-able.
  // eslint-disable-next-line no-console
  console.error('[reportClientError]', context.action ?? 'unknown', err, { ...context, recent_request_ids, recent_crumbs })
  void sendError({
    message,
    stack_trace: stack,
    location: window.location.href,
    user_agent: navigator.userAgent,
    context: {
      ...context,
      kind: 'caught',
      shape: describeThrown(err),
      recent_request_ids,
      recent_crumbs,
    },
  })
}

let lastReportTime = 0
let inflightCount = 0
const MIN_INTERVAL_MS = 2000
const MAX_INFLIGHT = 3

/**
 * Install global error handlers. Idempotent — calling twice (e.g.
 * from a future remount) re-binds onerror but keeps the same
 * throttle counters, so the rate limiting still holds.
 *
 * Call from main.tsx before render so we catch errors during the
 * first paint.
 */
export function installErrorCapture(): void {
  // window.onerror — synchronous JS errors during evaluation,
  // event handlers, setTimeout callbacks, etc. The 5-arg signature
  // is the legacy DOM API; modern browsers also support an
  // ErrorEvent on addEventListener('error') but the assignment
  // form is portable and good enough.
  window.onerror = (msg, url, line, col, error) => {
    const messageStr = typeof msg === 'string' ? msg : String(msg)
    void sendError({
      message: messageStr,
      stack_trace: error?.stack ?? null,
      location: window.location.href,
      user_agent: navigator.userAgent,
      context: { url, line, col },
    })
    return false // let the browser also log to console
  }

  // unhandledrejection — Promises that reject without a .catch.
  // Cheap to add and catches the bulk of async errors that
  // window.onerror misses.
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { message?: string; stack?: string } | string | null
    const message =
      reason && typeof reason === 'object' && reason.message
        ? reason.message
        : String(reason)
    const stack = reason && typeof reason === 'object' ? (reason.stack ?? null) : null
    void sendError({
      message,
      stack_trace: stack,
      location: window.location.href,
      user_agent: navigator.userAgent,
      context: { kind: 'unhandledrejection' },
    })
  })
}

async function sendError(payload: ErrorReportPayload): Promise<void> {
  // Throttle: max 1 report per MIN_INTERVAL_MS + max MAX_INFLIGHT
  // concurrent. The server has its own per-IP limiter (60/min) but
  // a misbehaving page can fire onerror dozens of times per second,
  // and we'd rather drop on the client than burn the limiter on
  // duplicates.
  const now = Date.now()
  if (now - lastReportTime < MIN_INTERVAL_MS) return
  if (inflightCount >= MAX_INFLIGHT) return
  lastReportTime = now
  // PR-31: when the page is unloading the browser will cancel any
  // in-flight `fetch`. `navigator.sendBeacon` is the platform's
  // explicit "ship this small POST and I don't care about the
  // response" primitive — designed for analytics/teardown reports
  // exactly like ours. Use it whenever the document is hidden
  // (covers tab close, navigation away, browser quit). The server
  // accepts the same JSON shape on /api/errors/report.
  // Note: sendBeacon doesn't carry credentials by default for
  // cross-origin, but our endpoint is same-origin (Vite proxy in
  // dev, served from the API host in prod), so cookies ride along.
  if (
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function'
  ) {
    try {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      })
      navigator.sendBeacon('/api/errors/report', blob)
    } catch {
      // sendBeacon throws synchronously only on quota exceeded —
      // nothing to do, the error is already lost.
    }
    return
  }
  inflightCount += 1
  try {
    await fetch('/api/errors/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
  } catch {
    // Swallow — reporting errors about reporting errors is silly,
    // and the server-side eprintln path will surface PG outages.
  } finally {
    inflightCount -= 1
  }
}
